import axios, { isAxiosError, isCancel, type CancelTokenSource } from 'axios';
import LOCAL_STORAGE_KEY from 'src/constants/localKeys';
import { removeHeaderFromDefaults, setDefaultHeaders } from 'src/agent';

import UserSevice from '../UserSevice';
import {
    configureAcquisitionAuthenticatedBindTransport,
    configurePurchaseIdentityTransport,
    setAcquisitionAuthenticatedSession,
    withAcquisitionHandoff,
} from '../AcquisitionBrowserBridge';

import {
    bindAcquisitionCapability,
    recordPurchaseIdentity,
    signIn,
    signUp,
    updateToken,
    getCode,
    confirmResetPassword,
    confirmEmail,
    refreshEmailCode,
} from './agent';

import type { IUserSignIn, IUserRegister, IUserResponse } from 'src/types/User';

interface AuthResponse {
    access?: string;
    refresh?: string;
    user?: IUserResponse;
}

function configureTokenHeader(token: string) {
    if (token) {
        setDefaultHeaders('Authorization', `Bearer ${token}`);
        return;
    }
    removeHeaderFromDefaults('Authorization');
}

class SessionService {
    queryKey = 'session';

    cancelToken: CancelTokenSource | undefined;

    constructor() {
        const token = localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN) || '';
        const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN) || '';

        this.accessTokenValue = token;
        this.refreshTokenValue = refreshToken;

        // Устанавливаем заголовок авторизации при инициализации
        if (token) {
            configureTokenHeader(token);
        }
    }

    private accessTokenValue = '';

    private refreshTokenValue = '';

    get accessToken() {
        if (this.accessTokenValue) {
            return this.accessTokenValue;
        }
        this.accessTokenValue = localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN) || '';
        return this.accessTokenValue;
    }

    set accessToken(token: string) {
        this.accessTokenValue = token;
        configureTokenHeader(token);
        localStorage.setItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN, token);
    }

    get refreshToken() {
        if (this.refreshTokenValue) {
            return this.refreshTokenValue;
        }
        this.refreshTokenValue = localStorage.getItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN) || '';
        return this.refreshTokenValue;
    }

    set refreshToken(token: string) {
        this.refreshTokenValue = token;
        localStorage.setItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN, token);
    }

    signup = async (values: IUserRegister) => {
        try {
            const { data } = await signUp(withAcquisitionHandoff(values));
            const authData = data as AuthResponse;

            if (authData.access) {
                this.accessToken = authData.access;

                if (authData.refresh) {
                    this.refreshToken = authData.refresh;
                }

                const user = await this.syncLeadsmaniaProfile();

                return Promise.resolve({ ...authData, user });
            }

            return Promise.resolve(data);
        } catch (error) {
            if (isAxiosError(error)) {
                const data: Record<string, unknown> = error.response?.data;
                return Promise.reject(data);
            }
            return Promise.reject(error);
        }
    };

    getUserSessionRequiredData = async () => {
        try {
            const user = await UserSevice.getUser();
            setAcquisitionAuthenticatedSession('authenticated', user.id);

            return Promise.resolve({
                user,
                subscriptions: [],
                collections: {
                    chat_bundles: [],
                    cities: [],
                    locations: [],
                },
                projects: [],
            });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getUserDataOnly = async () => {
        try {
            const user = await UserSevice.getUser();
            setAcquisitionAuthenticatedSession('authenticated', user.id);
            return Promise.resolve(user);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    syncLeadsmaniaProfile = async () => {
        try {
            const user = await UserSevice.getUser();
            setAcquisitionAuthenticatedSession('authenticated', user.id);
            return Promise.resolve(user);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    signin = async (values: IUserSignIn) => {
        try {
            const {
                data: { access: accessToken, refresh: refreshToken },
                data,
            } = await signIn(withAcquisitionHandoff(values));
            if (accessToken) {
                this.accessToken = accessToken;
                this.refreshToken = refreshToken;
            }
            const user = await this.syncLeadsmaniaProfile();

            return Promise.resolve({ ...data, user });
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const data = error.response?.data;
                return Promise.reject(data);
            }
            return Promise.reject(error);
        }
    };

    signOut = () => {
        setAcquisitionAuthenticatedSession('anonymous');
        this.accessToken = '';
        this.refreshToken = '';

        localStorage.removeItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
    };

    updateToken = async () => {
        try {
            const refreshToken = this.refreshToken;
            if (refreshToken) {
                const {
                    data: { access: accessToken },
                    data,
                } = await updateToken(refreshToken);
                this.accessToken = accessToken;
                return Promise.resolve({ ...data });
            }
            throw new Error('Рефреш токена нет');
        } catch (error) {
            this.signOut();
            return Promise.reject(error);
        }
    };

    resetPassword = async (values: Pick<IUserSignIn, 'email'>) => {
        try {
            if (this.cancelToken) {
                this.cancelToken.cancel();
            }

            this.cancelToken = axios.CancelToken.source();

            await getCode(values, this.cancelToken.token);

            return Promise.resolve({});
        } catch (error) {
            if (isCancel(error)) {
                return;
            }
            console.log(error);
            return Promise.reject(error);
        }
    };

    confirmResetPassword = async (
        values: { code: string } & Pick<IUserRegister, 'email' | 'password' | 'password2'>
    ) => {
        try {
            await confirmResetPassword({
                ...values,
                new_password: values.password,
                new_password2: values.password2,
            });

            return Promise.resolve({});
        } catch (error) {
            console.log(error);

            return Promise.reject(error);
        }
    };

    confirmEmail = async (values: { code: string } & Pick<IUserRegister, 'email'>) => {
        try {
            await confirmEmail(withAcquisitionHandoff(values));

            return Promise.resolve({});
        } catch (error) {
            console.log(error);

            return Promise.reject(error);
        }
    };

    refetchEmailCode = async () => {
        try {
            await refreshEmailCode();
            return Promise.resolve();
        } catch (error) {
            console.log(error);
            return Promise.reject(error);
        }
    };
}

const sessionService = new SessionService();

configureAcquisitionAuthenticatedBindTransport(async (capability) => {
    try {
        const response = await bindAcquisitionCapability(capability);
        return { status: response.status, data: response.data };
    } catch (error) {
        if (!isAxiosError(error)) return { status: 0 };
        const retryAfter = error.response?.headers?.['retry-after'];
        const retryAfterText =
            typeof retryAfter === 'string' || typeof retryAfter === 'number' ? String(retryAfter) : '';
        const retryAfterSeconds = Number(retryAfterText);
        const retryAfterMs = Number.isFinite(retryAfterSeconds)
            ? retryAfterSeconds * 1000
            : Date.parse(retryAfterText) - Date.now();
        return {
            status: error.response?.status || 0,
            retryAfterMs: Number.isFinite(retryAfterMs) ? Math.max(0, retryAfterMs) : 0,
        };
    }
});

configurePurchaseIdentityTransport(async (payload) => {
    try {
        const response = await recordPurchaseIdentity(payload);
        return { status: response.status, data: response.data };
    } catch (error) {
        if (!isAxiosError(error)) return { status: 0 };
        const retryAfter = error.response?.headers?.['retry-after'];
        const retryAfterText =
            typeof retryAfter === 'string' || typeof retryAfter === 'number' ? String(retryAfter) : '';
        const retryAfterSeconds = Number(retryAfterText);
        const retryAfterMs = Number.isFinite(retryAfterSeconds)
            ? retryAfterSeconds * 1000
            : Date.parse(retryAfterText) - Date.now();
        return {
            status: error.response?.status || 0,
            data: error.response?.data,
            retryAfterMs: Number.isFinite(retryAfterMs) ? Math.max(0, retryAfterMs) : 0,
        };
    }
});

setAcquisitionAuthenticatedSession(sessionService.accessToken || sessionService.refreshToken ? 'pending' : 'anonymous');

export default sessionService;
